import { useState, forwardRef } from 'react';

interface PhoneInputProps {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  name?: string;
  placeholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value = '', onChange, onBlur, name, placeholder = "(978) 705-55-34", className = '', error, disabled, autoFocus }, ref) => {
    const [displayValue, setDisplayValue] = useState(formatPhone(value));

    function formatPhone(phone: string): string {
      // Remove all non-digits
      const cleaned = phone.replace(/\D/g, '');
      
      // If starts with 8, replace with 7
      const normalized = cleaned.startsWith('8') ? '7' + cleaned.slice(1) : cleaned;
      
      // Format as: XXX (XXX) XX-XX (without leading 7)
      if (normalized.length === 0) return '';
      if (normalized.length === 1 && normalized === '7') return '';
      
      // Remove leading 7 for display
      const displayDigits = normalized.startsWith('7') ? normalized.slice(1) : normalized;
      
      if (displayDigits.length === 0) return '';
      if (displayDigits.length <= 3) return displayDigits;
      if (displayDigits.length <= 6) return `(${displayDigits.slice(0, 3)}) ${displayDigits.slice(3)}`;
      if (displayDigits.length <= 8) return `(${displayDigits.slice(0, 3)}) ${displayDigits.slice(3, 5)}-${displayDigits.slice(5)}`;
      if (displayDigits.length <= 10) return `(${displayDigits.slice(0, 3)}) ${displayDigits.slice(3, 5)}-${displayDigits.slice(5, 8)}-${displayDigits.slice(8)}`;
      return `(${displayDigits.slice(0, 3)}) ${displayDigits.slice(3, 5)}-${displayDigits.slice(5, 8)}-${displayDigits.slice(8, 10)}`;
    }

    function getRawPhone(formatted: string): string {
      const cleaned = formatted.replace(/\D/g, '');
      if (cleaned.length === 0) return '';
      if (cleaned.length === 10) return '7' + cleaned;
      if (cleaned.length === 11 && cleaned.startsWith('7')) return cleaned;
      if (cleaned.length === 11 && cleaned.startsWith('8')) return '7' + cleaned.slice(1);
      // Если 11 цифр начинаются с 9, берем первые 10 и добавляем 7
      if (cleaned.length === 11 && cleaned.startsWith('9')) return '7' + cleaned.slice(0, 10);
      return cleaned.length <= 11 ? cleaned : cleaned.slice(0, 11);
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const raw = getRawPhone(input);
      
      // Limit to 11 digits (7 + 10)
      if (raw.length <= 11) {
        const formatted = formatPhone(raw);
        setDisplayValue(formatted);
        
        // Pass the raw phone number through the event
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: raw
          }
        };
        onChange?.(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter
      if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
          // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          (e.keyCode === 65 && e.ctrlKey) ||
          (e.keyCode === 67 && e.ctrlKey) ||
          (e.keyCode === 86 && e.ctrlKey) ||
          (e.keyCode === 88 && e.ctrlKey) ||
          // Allow: home, end, left, right
          (e.keyCode >= 35 && e.keyCode <= 39)) {
        return;
      }
      // Ensure that it is a number and stop the keypress
      if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
      }
    };

    return (
      <div className="relative">
        <div className="flex">
          <div className="flex items-center px-3 py-3 bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg text-gray-600 font-medium">
            +7
          </div>
          <input
            ref={ref}
            type="tel"
            name={name}
            value={displayValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            className={`
              flex-1 px-4 py-3 border border-gray-300 rounded-r-lg 
              focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 
              transition-colors text-lg
              ${error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
              ${className}
            `}
          />
        </div>
        {error && (
          <p className="text-red-500 text-sm mt-1">{error}</p>
        )}
        <div className="text-xs text-gray-500 mt-1">
          Введите 10 цифр без +7
        </div>
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';